import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import { AxiosError } from 'axios'
import React, { useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectExpenseCategoryIdFromRoute,
  selectOrgIdFromRoute,
} from 'src/app/ExpenseCategoryLabels/redux/expenseCategoryLabels.selectors'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import { BackendErrorToastMessage } from 'src/app/Toast/Toast'
import LayoutPage from 'src/component-layout/LayoutPage/LayoutPage'
import LayoutTwoColumnPage from 'src/component-layout/LayoutTwoColumnPage/LayoutTwoColumnPage'
import ExpenseCategoryLabelsList from 'src/components/ExpenseCategoryLabelsList/ExpenseCategoryLabelsList'
import GenericLayoutDetailsComponent from 'src/components/GenericLayoutDetailsComponent/GenericLayoutDetailsComponent'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotFoundBackgroundPage from 'src/components/NotFoundBackgroundPage/NotFoundBackgroundPage'
import OnOffSwitchWithLabel from 'src/components/OnOffSwitchWithLabel/OnOffSwitchWithLabel'
import SvgUpload from 'src/components/Svgs/SvgUpload'
import TextDropdown from 'src/components/TextDropdown/TextDropdown'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import { useFormFields, useQueryParamArrayHandler } from 'src/hooks'
import Trns, { translateSingleString } from 'src/localization/Trns'
import { OrganizationIntegrationListModel } from 'src/models/organizationIntegration.models'
import { UserFeature } from 'src/models/user.models'
import { bulkUploadExpenseLabelsUrl } from 'src/navigation/urls'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import {
  ExpenseCategoryType,
  PatchExpenseCategoryRequest,
  usePatchExpenseCategory,
  useQueryExpenseCategory,
} from 'src/queries/organizations/expenseCategories.queries'
import { useQueryOwnUser } from 'src/queries/users.queries'
import { getOrganizationIntegrations } from 'src/redux/actions/data/organizations.actions'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import {
  selectActiveOrgIntegration,
  selectFormattedOrgIntegrationLastSyncDate,
} from 'src/redux/selectors/data/orgIntegrations.selectors'
import { selectQueryParams } from 'src/redux/selectors/router.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import { BackendErrorPayload } from 'src/utils/error.utils'
import { stringIsEmpty } from 'src/utils/validation/string.validation'
import styled, { css } from 'styled-components'

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
`

const Size20Span = styled.span`
  ${getFontCss(
    20,
    themeConstants.colors.font_primary,
    styleConstants.fontFamilies.medium,
  )}
`
const Size14Span = styled.span`
  ${getFontCss(
    14,
    styleConstants.colors.gray_darkest,
    styleConstants.fontFamilies.regular,
  )}
`
const Size16Span = styled.span`
  ${getFontCss(
    16,
    themeConstants.colors.font_primary,
    styleConstants.fontFamilies.regular,
  )}
`

const ExpenseCategoryNameLabelAndValue: React.FC<{ value: string }> = ({
  value,
}) => {
  return (
    <FlexColumn
      css={css`
        gap: 8px;
      `}
    >
      <Size14Span>
        <Trns source={'Expense Category'} />
      </Size14Span>
      <Size20Span data-testid={'expenseCategoryNameStaticValue'}>
        {value}
      </Size20Span>
    </FlexColumn>
  )
}
const ExpenseCategoryCodeLabelAndValue: React.FC<{ value: string }> = ({
  value,
}) => {
  return (
    <FlexColumn
      css={css`
        gap: 8px;
      `}
    >
      <Size14Span>
        <Trns source={'Category Code'} />
      </Size14Span>
      <Size16Span data-testid={'expenseCategoryCodeStaticValue'}>
        {value}
      </Size16Span>
    </FlexColumn>
  )
}
const ConnectStatusLabelAndValue: React.FC<{
  activeIntegration: OrganizationIntegrationListModel
  lastSyncDate: string
}> = ({ activeIntegration, lastSyncDate }) => {
  return (
    <FlexColumn
      css={css`
        gap: 8px;
      `}
    >
      <Size14Span>
        <Trns source={'Connection Status'} />
      </Size14Span>
      <div
        css={css`
          display: flex;
          gap: 8px;
          align-items: center;
        `}
      >
        {activeIntegration.iconElement ? (
          <span
            css={css`
              width: 20px;
              height: 20px;
              display: inline-flex;
              align-items: center;
              justify-content: center;

              svg {
                width: 100%;
                height: 100%;
              }
            `}
            aria-hidden="true"
          >
            {activeIntegration.iconElement}
          </span>
        ) : (
          <img
            css={css`
              width: 20px;
              height: 20px;
            `}
            src={activeIntegration.logo}
            alt={activeIntegration.platformName}
          />
        )}
        <span
          css={css`
            ${getFontCss(
              12,
              styleConstants.colors.gray_darkest,
              styleConstants.fontFamilies.regular,
            )};
          `}
        >
          <Trns
            source={'Last refreshed ${date}'}
            expressions={{ date: lastSyncDate }}
          />
        </span>
      </div>
    </FlexColumn>
  )
}

const SendToBankLabel: React.FC<{ appName: string }> = ({ appName }) => (
  <div
    css={css`
      display: flex;
      flex-direction: column;
      gap: 8px;
      margin-top: 2px;
      flex: 1;
    `}
  >
    <span
      css={css`
        ${getFontCss(
          14,
          themeConstants.colors.font_primary,
          styleConstants.fontFamilies.medium,
        )};
      `}
    >
      <Trns source={'Send to Bank'} />
    </span>
    <span
      css={css`
        ${getFontCss(
          12,
          styleConstants.colors.gray_darkest,
          styleConstants.fontFamilies.regular,
        )};
      `}
    >
      <Trns
        source={
          '${appName} will automatically send expense category data to your bank when you create a virtual card. This feature is typically not used by most companies.'
        }
        expressions={{ appName }}
      />
    </span>
  </div>
)

const ExpenseCategoryLabels: React.FC = () => {
  const locale = useSelector(selectNullSafeLocaleFromState)
  const dispatch = useDispatch()
  const queryParams = useSelector(selectQueryParams)
  const { data: currentUser, isPending: currentUserInitialLoading } =
    useQueryOwnUser({})
  const userCanViewPage = !!currentUser?.hasOrgBookkeeperPermissions

  const appName = useSelector(selectAppName)
  const orgId = useSelector(selectOrgIdFromRoute)
  const expenseCategoryId = useSelector(selectExpenseCategoryIdFromRoute)
  const activeIntegration = useSelector(selectActiveOrgIntegration)
  const lastSyncDate = useSelector(selectFormattedOrgIntegrationLastSyncDate)

  const {
    data: expenseCategory,
    isPending: expenseCategoryInitialLoading,
    error: expenseCategoryError,
  } = useQueryExpenseCategory({
    expenseCategoryId,
    enabled: userCanViewPage,
    retry: false,
  })
  const { mutateAsync: patchExpCat, isPending: patchExpCatLoading } =
    usePatchExpenseCategory()

  const { EDIT_EXPENSE_CATEGORIES: hasEditExpCatPermissions } =
    useQueryUserFeatures([UserFeature.EDIT_EXPENSE_CATEGORIES]).data || {}

  const patchExpCatSucceeded = async ({
    id,
    body,
  }: {
    id: string
    body: PatchExpenseCategoryRequest
  }): Promise<boolean> => {
    return await patchExpCat({ id, body })
      .then(() => true)
      .catch((e: AxiosError<BackendErrorPayload<string>>) => {
        dispatch(
          createSetIsToastOpen(true, {
            toastMessage: <BackendErrorToastMessage error={e} />,
            failureColorScheme: true,
            hideImage: true,
          }),
        )
        return false
      })
  }

  const [addingNewLabel, setAddingNewLabel] = React.useState<boolean>(false)

  useEffect((): void => {
    if (orgId) {
      dispatch(getOrganizationIntegrations(orgId))
    }
  }, [])

  useEffect(() => {
    if (expenseCategory !== undefined) {
      onFieldChange('categoryName', expenseCategory.name)
    }
  }, [expenseCategory])

  const {
    fields,
    fieldsErrors,
    onFieldChange,
    setFieldsErrors,
    setErrorsAndReturnDidPassValidation,
  } = useFormFields<{ categoryName: string }>({ categoryName: '' }, () => ({
    categoryName: [stringIsEmpty()],
  }))

  if (currentUserInitialLoading || expenseCategoryInitialLoading)
    return <LoadingBackgroundPage />
  if (!userCanViewPage || expenseCategoryError)
    return <NotFoundBackgroundPage />

  return (
    <>
      <Helmet>
        <title>{`Expense Category Labels | ${appName}`}</title>
        <meta
          name="description"
          content={`Expense Category Labels | ${appName}`}
        />
      </Helmet>
      <LayoutPage padding={'medium'}>
        <LayoutTwoColumnPage
          detailsComponent={
            <GenericLayoutDetailsComponent
              staticContent={
                <FlexColumn
                  css={css`
                    gap: 20px;
                  `}
                >
                  <ExpenseCategoryNameLabelAndValue
                    value={expenseCategory?.name || '--'}
                  />
                  <ExpenseCategoryCodeLabelAndValue
                    value={expenseCategory?.code || '--'}
                  />
                  {activeIntegration && (
                    <ConnectStatusLabelAndValue
                      activeIntegration={activeIntegration}
                      lastSyncDate={lastSyncDate}
                    />
                  )}
                </FlexColumn>
              }
              updatingContent={
                <FlexColumn
                  css={css`
                    gap: 20px;
                  `}
                >
                  <TextInputField
                    testId={'expenseCategoryNameInput'}
                    fieldLabel={<Trns source={'Expense Category'} />}
                    value={fields.categoryName}
                    baseValue={expenseCategory?.name || ''}
                    errors={fieldsErrors.categoryName}
                    design={TextInputFieldDesigns.New}
                    placeholder={translateSingleString(locale, 'Enter a name')}
                    onChange={(value): void =>
                      onFieldChange('categoryName', value)
                    }
                    disabled={patchExpCatLoading}
                  />
                  <ExpenseCategoryCodeLabelAndValue
                    value={expenseCategory?.code || '--'}
                  />
                  {activeIntegration && (
                    <ConnectStatusLabelAndValue
                      activeIntegration={activeIntegration}
                      lastSyncDate={lastSyncDate}
                    />
                  )}
                </FlexColumn>
              }
              saveLoading={patchExpCatLoading}
              onSave={(): boolean | Promise<boolean> => {
                if (!setErrorsAndReturnDidPassValidation()) return false

                return patchExpCat({
                  id: expenseCategoryId,
                  body: { name: fields.categoryName },
                })
                  .then(() => {
                    return true
                  })
                  .catch(() => {
                    return false
                  })
              }}
              onCancel={(): void => {
                onFieldChange('categoryName', expenseCategory?.name || '')
                setFieldsErrors({ categoryName: undefined })
              }}
              disallowUpdate={
                expenseCategory?.editable === false || !hasEditExpCatPermissions
              }
              additionalContent={
                <FlexColumn
                  css={css`
                    gap: 40px;
                  `}
                >
                  <OnOffSwitchWithLabel
                    label={<Trns source={'Active'} />}
                    subLabel={
                      <Trns
                        source={
                          'When active, everyone in the org can code transactions to the category. When inactive, the category will be hidden from view.'
                        }
                      />
                    }
                    defaultOnOffState={expenseCategory?.active}
                    testId={'expenseCategoryActiveSwitch'}
                    onChange={(value): Promise<boolean> =>
                      patchExpCatSucceeded({
                        id: expenseCategoryId,
                        body: { active: value },
                      })
                    }
                    disabled={
                      (activeIntegration !== undefined &&
                        expenseCategory?.type ===
                          ExpenseCategoryType.Account) ||
                      !hasEditExpCatPermissions
                    }
                    tooltipProps={{
                      tooltipContent:
                        activeIntegration !== undefined &&
                        expenseCategory?.type ===
                          ExpenseCategoryType.Account ? (
                          <Trns
                            source={
                              'This setting is locked because your organization is connected to accounting software'
                            }
                          />
                        ) : undefined,
                      placement: 'bottom-end',
                    }}
                  />
                  <OnOffSwitchWithLabel
                    label={<Trns source={'Required for Virtual Cards'} />}
                    subLabel={
                      <Trns
                        source={
                          'This will require all virtual cards to be coded with a value from this category.'
                        }
                      />
                    }
                    defaultOnOffState={expenseCategory?.requiredForVirtualCards}
                    testId={'expenseCategoryRequiredSwitchVirtualCards'}
                    onChange={(value): Promise<boolean> =>
                      patchExpCatSucceeded({
                        id: expenseCategoryId,
                        body: { requiredForVirtualCards: value },
                      })
                    }
                    disabled={!hasEditExpCatPermissions}
                  />
                  <OnOffSwitchWithLabel
                    label={<Trns source={'Required for Transactions'} />}
                    subLabel={
                      <Trns
                        source={
                          'This will require all transactions to be coded with a value from this category.'
                        }
                      />
                    }
                    defaultOnOffState={expenseCategory?.requiredForTransactions}
                    testId={'expenseCategoryRequiredSwitchTransactions'}
                    onChange={(value): Promise<boolean> =>
                      patchExpCatSucceeded({
                        id: expenseCategoryId,
                        body: { requiredForTransactions: value },
                      })
                    }
                    disabled={
                      (activeIntegration !== undefined &&
                        expenseCategory?.type ===
                          ExpenseCategoryType.Account) ||
                      !hasEditExpCatPermissions
                    }
                    tooltipProps={{
                      tooltipContent:
                        activeIntegration !== undefined &&
                        expenseCategory?.type ===
                          ExpenseCategoryType.Account ? (
                          <Trns
                            source={
                              'This setting is locked because your organization is connected to accounting software'
                            }
                          />
                        ) : undefined,
                      placement: 'bottom-end',
                    }}
                  />
                  {activeIntegration === undefined && (
                    <OnOffSwitchWithLabel
                      label={<Trns source={'Free Text'} />}
                      subLabel={
                        <Trns
                          source={
                            'Enabling this will allow members to enter their own options when tagging virtual cards and transactions.'
                          }
                        />
                      }
                      defaultOnOffState={expenseCategory?.freeTextAllowed}
                      testId={'expenseCategoryFreeTextSwitch'}
                      onChange={(value): Promise<boolean> =>
                        patchExpCatSucceeded({
                          id: expenseCategoryId,
                          body: { freeTextAllowed: value },
                        })
                      }
                      disabled={!hasEditExpCatPermissions}
                    />
                  )}
                  <FlexColumn
                    css={css`
                      gap: 20px;
                    `}
                  >
                    <SendToBankLabel appName={appName} />
                    <TextDropdown
                      deselectAllOptions={(): void => {
                        patchExpCatSucceeded({
                          id: expenseCategoryId,
                          body: { integratorEnabled: false },
                        })
                      }}
                      css={css`
                        margin-left: 20px;
                      `}
                      data-testid={'expenseCategoryFieldNumberDropdown'}
                      placeholder={translateSingleString(
                        locale,
                        'Select Field',
                      )}
                      fieldLabel={<Trns source={'Field Number'} />}
                      chooseOption={(val: string): Promise<boolean> =>
                        patchExpCatSucceeded({
                          id: expenseCategoryId,
                          body: {
                            integratorEnabled: true,
                            integratorFieldNumber: parseInt(val),
                          },
                        })
                      }
                      isMulti={false}
                      disabled={patchExpCatLoading || !hasEditExpCatPermissions}
                      selectedValues={
                        expenseCategory?.integratorEnabled &&
                        expenseCategory?.integratorFieldNumber
                          ? new Set([
                              expenseCategory?.integratorFieldNumber.toString(),
                            ])
                          : new Set()
                      }
                      optionGroups={[
                        {
                          options: [
                            {
                              value: '1',
                              details: {
                                text: translateSingleString(locale, 'Field 1'),
                              },
                            },
                            {
                              value: '2',
                              details: {
                                text: translateSingleString(locale, 'Field 2'),
                              },
                            },
                            {
                              value: '3',
                              details: {
                                text: translateSingleString(locale, 'Field 3'),
                              },
                            },
                            {
                              value: '4',
                              details: {
                                text: translateSingleString(locale, 'Field 4'),
                              },
                            },
                            {
                              value: '5',
                              details: {
                                text: translateSingleString(locale, 'Field 5'),
                              },
                            },
                          ],
                        },
                      ]}
                    />
                  </FlexColumn>
                </FlexColumn>
              }
            />
          }
          listComponent={
            <div className={'flex-column gap-6 w-full h-full'}>
              <div
                className={'flex-row align-center justify-between gap-8 w-full'}
              >
                <span
                  css={css`
                    ${getFontCss(
                      20,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.medium,
                    )}
                  `}
                >
                  {translateSingleString(locale, 'Labels')}
                </span>
                {expenseCategory?.editable && hasEditExpCatPermissions && (
                  <HoverableLink
                    to={bulkUploadExpenseLabelsUrl(orgId, expenseCategoryId)}
                    ariaLabel={translateSingleString(locale, 'Bulk Upload')}
                  >
                    <div className={'flex-row align-center gap-2'}>
                      <SvgUpload width={12} height={12} viewBox={'0 0 16 16'} />
                      {translateSingleString(locale, 'Bulk Upload')}
                    </div>
                  </HoverableLink>
                )}
              </div>
              <ExpenseCategoryLabelsList
                initialFilterValues={{
                  search: (
                    useQueryParamArrayHandler(queryParams.search) as string[]
                  )?.[0],
                  status: useQueryParamArrayHandler(
                    queryParams.status,
                  ) as string[],
                }}
                expenseCategoryId={expenseCategoryId}
                addingNewLabel={addingNewLabel}
                onAddingNewLabelChange={setAddingNewLabel}
              />
            </div>
          }
        />
      </LayoutPage>
    </>
  )
}

export default ExpenseCategoryLabels
